import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  CreateProductDto,
  IPermission,
  IPermissionGroup,
  IPermissionImportResult,
  IProduct,
  PermissionFormValues,
  UpdateProductDto,
} from '~/Services/Product.interface'
import { PageMeta } from '~/Types/Common'
import { ModalName } from './constants'

interface ModalVisibility {
  name: string
  visible: boolean
}

interface ProductState {
  isLoading: boolean
  isConfirmLoading: boolean
  products: IProduct[]
  productSelected?: IProduct
  modalVisibility: {
    [id: string]: { visible: boolean }
  }
  // Import Permissions modal — luôn gắn với đúng 1 Product (row được click), không cho chọn lại.
  importTarget?: IProduct
  isImporting: boolean
  importResult?: IPermissionImportResult

  // ---- Product Detail / Permission Viewer ----
  isLoadingPermissionGroups: boolean
  permissionGroups: IPermissionGroup[]
  isLoadingPermissions: boolean
  permissions: IPermission[]
  permissionsPageMeta?: PageMeta

  // Add/Edit Permission modal — undefined permissionSelected means "create" (mirrors Product's own
  // productSelected convention). isMutatingPermission covers create/update/delete/bulk-delete.
  permissionSelected?: IPermission
  isMutatingPermission: boolean
  // Bumped on every successful create/update/delete/bulk-delete — ProductDetail watches this to
  // refetch with whatever search/group/page it currently has, instead of the slice guessing params.
  permissionsVersion: number
}

const initialState: ProductState = {
  isLoading: false,
  isConfirmLoading: false,
  products: [],
  modalVisibility: {},
  isImporting: false,
  isLoadingPermissionGroups: false,
  permissionGroups: [],
  isLoadingPermissions: false,
  permissions: [],
  isMutatingPermission: false,
  permissionsVersion: 0,
}

export const ProductSlice = createSlice({
  name: 'product',
  initialState,
  reducers: {
    getProducts: state => {
      state.isLoading = true
    },
    getProductsSuccess: (state, action: PayloadAction<IProduct[]>) => {
      state.products = action.payload
      state.isLoading = false
    },
    getProductsFail: state => {
      state.isLoading = false
    },
    setModalVisibility: (state, action: PayloadAction<ModalVisibility>) => {
      const { name, visible } = action.payload
      state.modalVisibility[name] = { visible }
    },
    setProductSelected: (state, action: PayloadAction<IProduct | undefined>) => {
      state.productSelected = action.payload
    },
    createProduct: (state, _action: PayloadAction<CreateProductDto>) => {
      state.isConfirmLoading = true
    },
    createProductSuccess: state => {
      state.isConfirmLoading = false
      state.modalVisibility[ModalName.Product] = { visible: false }
    },
    createProductFail: state => {
      state.isConfirmLoading = false
    },
    updateProduct: (
      state,
      // silent: skip the success toast — ProductFormModal sets it when an application update is sent in
      // the same Save, so the user sees one "updated" message instead of two.
      _action: PayloadAction<{ id: string; data: UpdateProductDto; silent?: boolean }>,
    ) => {
      state.isConfirmLoading = true
    },
    updateProductSuccess: state => {
      state.isConfirmLoading = false
      state.modalVisibility[ModalName.Product] = { visible: false }
    },
    updateProductFail: state => {
      state.isConfirmLoading = false
    },

    // ---- Import Permissions ----
    openImportModal: (state, action: PayloadAction<IProduct>) => {
      state.importTarget = action.payload
      state.importResult = undefined
      state.isImporting = false
    },
    closeImportModal: state => {
      state.importTarget = undefined
      state.importResult = undefined
      state.isImporting = false
    },
    importPermissions: (
      state,
      _action: PayloadAction<{ productId: string; file: File }>,
    ) => {
      state.isImporting = true
      state.importResult = undefined
    },
    // Dùng chung cho cả 200 (thành công) lẫn 400 workbook-validation — backend trả cùng shape (success=false
    // + errors[]), hiển thị structured result thay vì toast chung.
    importPermissionsSuccess: (
      state,
      action: PayloadAction<IPermissionImportResult>,
    ) => {
      state.isImporting = false
      state.importResult = action.payload
    },
    // Chỉ dùng cho lỗi KHÔNG có structured result (404 Product not found, 401/403, 500, network error) —
    // đã có toast riêng qua errorNotification ở epic, action này chỉ để tắt loading state.
    importPermissionsFail: state => {
      state.isImporting = false
    },

    // ---- Product Detail / Permission Viewer ----
    getPermissionGroups: (state, _action: PayloadAction<string>) => {
      state.isLoadingPermissionGroups = true
    },
    getPermissionGroupsSuccess: (
      state,
      action: PayloadAction<IPermissionGroup[]>,
    ) => {
      state.permissionGroups = action.payload
      state.isLoadingPermissionGroups = false
    },
    getPermissionGroupsFail: state => {
      state.isLoadingPermissionGroups = false
    },
    searchPermissions: (
      state,
      _action: PayloadAction<{
        productId: string
        search?: string
        group?: string
        page: number
        pageSize: number
      }>,
    ) => {
      state.isLoadingPermissions = true
    },
    searchPermissionsSuccess: (state, action: PayloadAction<any>) => {
      const {
        results,
        page,
        pageCount,
        pageSize,
        queryCount,
        firstRowIndex,
        lastRowIndex,
      } = action.payload
      state.permissions = results
      state.permissionsPageMeta = {
        page,
        pageCount,
        pageSize,
        queryCount,
        firstRowIndex,
        lastRowIndex,
      }
      state.isLoadingPermissions = false
    },
    searchPermissionsFail: state => {
      state.isLoadingPermissions = false
    },
    // Gọi khi rời Product Detail — tránh hiển thị nhầm group/permission của Product trước đó
    // trong khoảnh khắc trước khi request mới của Product sau trả về.
    resetProductDetail: state => {
      state.permissionGroups = []
      state.permissions = []
      state.permissionsPageMeta = undefined
    },

    // ---- Add/Edit/Delete Permission ----
    setPermissionSelected: (state, action: PayloadAction<IPermission | undefined>) => {
      state.permissionSelected = action.payload
    },
    createPermission: (
      state,
      _action: PayloadAction<{ productId: string; data: PermissionFormValues }>,
    ) => {
      state.isMutatingPermission = true
    },
    createPermissionSuccess: state => {
      state.isMutatingPermission = false
      state.modalVisibility[ModalName.Permission] = { visible: false }
      state.permissionsVersion += 1
    },
    createPermissionFail: state => {
      state.isMutatingPermission = false
    },
    updatePermission: (
      state,
      _action: PayloadAction<{ productId: string; permissionId: string; data: PermissionFormValues }>,
    ) => {
      state.isMutatingPermission = true
    },
    updatePermissionSuccess: state => {
      state.isMutatingPermission = false
      state.modalVisibility[ModalName.Permission] = { visible: false }
      state.permissionSelected = undefined
      state.permissionsVersion += 1
    },
    updatePermissionFail: state => {
      state.isMutatingPermission = false
    },
    deletePermission: (
      state,
      _action: PayloadAction<{ productId: string; permissionId: string }>,
    ) => {
      state.isMutatingPermission = true
    },
    deletePermissionSuccess: state => {
      state.isMutatingPermission = false
      state.permissionsVersion += 1
    },
    deletePermissionFail: state => {
      state.isMutatingPermission = false
    },
    bulkDeletePermissions: (
      state,
      _action: PayloadAction<{ productId: string; permissionIds: string[] }>,
    ) => {
      state.isMutatingPermission = true
    },
    bulkDeletePermissionsSuccess: state => {
      state.isMutatingPermission = false
      state.permissionsVersion += 1
    },
    bulkDeletePermissionsFail: state => {
      state.isMutatingPermission = false
    },
  },
})

export const ProductActions = ProductSlice.actions
export const ProductReducer = ProductSlice.reducer
